import { FC, Fragment, MouseEventHandler, useContext, useEffect, useMemo, useRef, useState } from "react";
import Section from "components/gonzo/parts/Section";
import AnchorTarget from "components/official/AnchorTarget";
import { LibraryContext } from "lib/context/gonzo/LibraryContext";
import styled from "styled-components";
import Headline from "components/gonzo/parts/Headline";
import Description from "components/gonzo/parts/Description";
import Content from "components/gonzo/parts/Content";
import BreakPc from "components/gonzo/parts/BreakPc";
import { CSSTransition } from "react-transition-group";

const CrowdFunding: FC = () => {
  const crowdFundingPageUrl = useMemo(() => `${process.env.BASE_URL}/project/gonzo30th`, []);
  useAccordion();
  return (
    <Section>
      <AnchorTarget id="crowdfunding" />
      <Headline
        src={["/image/lp/gonzo/crowdfunding/crofun_title_1.png", "/image/lp/gonzo/crowdfunding/crofun_title_2.png"]}
        alt="クラウドファンディング"
      />
      <Content>
        <StartCountDownArea>
          <AlarmIcon viewBox="0 0 24 24" fill="white">
            <path d="M22 5.72l-4.6-3.86-1.29 1.53 4.6 3.86L22 5.72zM7.88 3.39L6.6 1.86 2 5.71l1.29 1.53 4.59-3.85zM12.5 8H11v6l4.75 2.85.75-1.23-4-2.37V8zM12 4c-4.97 0-9 4.03-9 9s4.02 9 9 9c4.97 0 9-4.03 9-9s-4.03-9-9-9zm0 16c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
          </AlarmIcon>
          <div>
            <TextRow>クラファン開始：9月11日(日) 18時〜</TextRow>
          </div>
        </StartCountDownArea>
        <Description>
          『GONZO30周年アニバーサリー』をファンの皆様に楽しんでもらいたい、
          <BreakPc />
          そして、ファンの皆様のお力をお借りして大きなイベントを実現したいという想いから、
          <BreakPc />
          クラウドファンディングを企画いたしました！
          <br />
          <br />
          クラウドファンディングでしか手に入らない限定商品（複製原画等）をお手にとっていただきながら、
          <BreakPc />
          皆様からの応援額に応じて「オンライントークイベント」や
          <BreakPc />
          「無料のオンライン展示会」を開催予定です！
          <br />
          様々なGONZO作品を振り返りながら、ぜひ応援いただけますと幸いです。
        </Description>
        <BannerArea>
          <StretchGoalBanner src="/image/lp/gonzo/crowdfunding/cf_stretchgoal_100.jpg" alt="ストレッチゴールのバナー" />
        </BannerArea>
        <AccordionRoot id="accordion">
          {ANIME_COURSES.map((animeCourse, index) => (
            <Fragment key={index}>
              <AccordionHeader className="accordionHeader">
                <TitleImage src={`/image/lp/gonzo/crowdfunding/image/${animeCourse.logo}`} />
                <Arrow src="/image/lp/gonzo/crowdfunding/image/crofun_accord_down.png" />
              </AccordionHeader>
              <AccordionContent>
                <CourseList>
                  {animeCourse.images.map((image, courseIndex) => (
                    <CourseItem key={`${index}_${courseIndex}`} image={image} />
                  ))}
                </CourseList>
                <LinkArea>
                  <CrowdFundingLink href={crowdFundingPageUrl}>クラウドファンディングページへ»</CrowdFundingLink>
                </LinkArea>
              </AccordionContent>
            </Fragment>
          ))}
        </AccordionRoot>
      </Content>
    </Section>
  );
};
export default CrowdFunding;

const CourseItem: FC<{ image: string }> = ({ image }) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState(false);
  const open = () => {
    document.body.style.height = "100vh";
    document.body.style.overflow = "hidden";
    setOpened(true);
  };
  const close = () => {
    document.body.style.height = "";
    document.body.style.overflow = "";
    setOpened(false);
  };
  const stopPropagation: MouseEventHandler<HTMLImageElement> = (e) => e.stopPropagation();
  return (
    <CourseImageBox>
      <CourseImage src={`/image/lp/gonzo/crowdfunding/image/${image}`} onClick={open} />
      <CSSTransition nodeRef={popupRef} in={opened} timeout={160} classNames="fade">
        <PopupContainer ref={popupRef} onClick={close}>
          <Popup src={`/image/lp/gonzo/crowdfunding/image/${image}`} onClick={stopPropagation} />
        </PopupContainer>
      </CSSTransition>
    </CourseImageBox>
  );
};

const ANIME_COURSES = [
  { logo: "cf_title_01.png", images: ["cf_image_01_01.jpg", "cf_image_01_02.jpg"] },
  { logo: "cf_title_02.png", images: ["cf_image_02_01.jpg", "cf_image_02_02.jpg"] },
  { logo: "cf_title_03.png", images: ["cf_image_03_01.jpg", "cf_image_03_02.jpg"] },
  { logo: "cf_title_04.png", images: ["cf_image_04_01.jpg", "cf_image_04_02.jpg"] },
  { logo: "cf_title_05.png", images: ["cf_image_05_01.jpg", "cf_image_05_02.jpg"] },
  { logo: "cf_title_06.png", images: ["cf_image_06_01.jpg", "cf_image_06_02.jpg"] },
  { logo: "cf_title_07.png", images: ["cf_image_07_01.jpg", "cf_image_07_02.jpg"] },
  { logo: "cf_title_08.png", images: ["cf_image_08_01.jpg", "cf_image_08_02.jpg"] },
  { logo: "cf_title_09.png", images: ["cf_image_09_01.jpg", "cf_image_09_02.jpg"] },
  { logo: "cf_title_10.png", images: ["cf_image_10_01.jpg", "cf_image_10_02.jpg"] },
  { logo: "cf_title_11.png", images: ["cf_image_11_01.jpg", "cf_image_11_02.jpg"] },
  { logo: "cf_title_12.png", images: ["cf_image_12_01.jpg", "cf_image_12_02.jpg"] },
  { logo: "cf_title_13.png", images: ["cf_image_13_01.jpg", "cf_image_13_02.jpg"] },
  { logo: "cf_title_14.png", images: ["cf_image_14_01.jpg", "cf_image_14_02.jpg"] },
];

const useAccordion = () => {
  const { jquery } = useContext(LibraryContext);
  useEffect(() => {
    if (jquery) {
      $("#accordion").accordion({
        icons: false,
        header: ".accordionHeader",
        animate: 200,
        heightStyle: "content",
        collapsible: true,
      });
    }
  }, [jquery]);
};

const AccordionRoot = styled.div`
  color: white;
  border-bottom: solid 2px white; ;
`;

const Arrow = styled.img`
  transition: transform 0.2s;
  transform-origin: center center;
  @media screen and (max-width: 960px) {
    width: 16px;
    height: 10px;
  }
  @media screen and (min-width: 961px) {
    width: 26px;
    height: 16px;
  }
`;

const AccordionHeader = styled.h3`
  border-top: solid 2px white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 20px;
  cursor: pointer;

  &.accordionHeader.ui-state-active {
    ${Arrow} {
      transform: rotate(180deg);
    }
  }
`;

const TitleImage = styled.img`
  @media screen and (max-width: 960px) {
    height: 44px;
  }
  @media screen and (min-width: 961px) {
    height: 80px;
  }
`;

const AccordionContent = styled.div`
  padding-bottom: 30px;
`;

const CourseList = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  @media screen and (max-width: 960px) {
    padding: 0 40px;
  }
  @media screen and (max-width: 960px) {
    flex-direction: column;
  }
`;

const AlarmIcon = styled.svg`
  width: 50px;
  height: 50px;
  margin-right: 10px;
`;
const StartCountDownArea = styled.div`
  background-color: #555;
  color: #fff;
  padding: 20px 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  @media screen and (max-width: 960px) {
    width: 100%;
    font-size: 14px;
    margin: 0 auto 20px;
  }
  @media screen and (min-width: 961px) {
    width: 70%;
    font-size: 16px;
    margin: 0 auto 50px;
  }
`;

const TextRow = styled.div`
  &:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const BannerArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  @media screen and (max-width: 960px) {
    margin-bottom: 20px;
  }
  @media screen and (min-width: 961px) {
    margin-bottom: 50px;
  }

  & > * {
    margin: 0 20px 20px;
  }
`;
const StretchGoalBanner = styled.img`
  @media screen and (max-width: 960px) {
    width: 100%;
  }
  @media screen and (min-width: 961px) {
    width: 70%;
  }
  color: white;
`;

const CourseImageBox = styled.div`
  @media screen and (max-width: 960px) {
    width: 100%;
    margin: 2% 0;
  }
  @media screen and (min-width: 961px) {
    width: 46%;
    margin: 0 2% 2%;
  }
`;

const CourseImage = styled.img`
  cursor: pointer;
  width: 100%;
`;

const PopupContainer = styled.div`
  background-color: rgba(10, 10, 10, 0.8);
  position: fixed;
  left: 0;
  width: 100%;
  z-index: 3;
  @media screen and (max-width: 960px) {
    height: calc(100vh - 50px);
    top: 50px;
  }
  @media screen and (min-width: 961px) {
    height: calc(100vh - 70px);
    top: 70px;
  }
  transition: opacity 0.2s;
  opacity: 0;
  visibility: hidden;

  &.fade-enter-active {
    opacity: 1;
    visibility: visible;
  }

  &.fade-enter-done {
    opacity: 1;
    visibility: visible;
  }

  &.fade-exit-active {
    opacity: 0;
    visibility: visible;
  }

  &.fade-exit-done {
    opacity: 0;
    visibility: hidden;
  }
`;

const Popup = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  @media screen and (max-width: 960px) {
    width: 100%;
  }
  @media screen and (min-width: 961px) {
    width: 900px;
  }
  @media screen and (min-width: 1281px) {
    width: 1200px;
  }
`;

const LinkArea = styled.div`
  text-align: right;
  @media screen and (max-width: 960px) {
    margin-top: 10px;
  }
  @media screen and (min-width: 961px) {
    padding: 0 30px;
  }
`;

const CrowdFundingLink = styled.a`
  font-size: 1.2em;
  cursor: pointer;
  color: white;
  text-decoration: underline;
`;
